import toLower from 'lodash/toLower'
import type { PivotLayoutValueColumn } from '@ztechapps/common.contract'
import type { PivotLeafColumn, PivotResult } from './types'
import { isPivotCalculatedMeasure } from '@ztechapps/common.utils'

/** Turns a leaf measure column into its layout value-column address. */
export const toPivotLayoutValueColumn = (result: PivotResult, column: PivotLeafColumn): PivotLayoutValueColumn => {
  const measure = result.measures[column.measureIndex]
  return {
    // the PivotNode key encoding: the member keys from the root down
    columnPath: (JSON.parse(column.columnKey) as string[]).map((key, level) => ({
      ...result.columnFields[level],
      key,
    })),
    // a calculated measure has no aggregation — its name alone addresses it
    measure: isPivotCalculatedMeasure(measure)
      ? { field: measure.field }
      : { field: measure.field, aggregation: measure.aggregation },
  }
}

/** Compares two value columns, ignoring member key letter case. */
export const isSamePivotValueColumn = (a: PivotLayoutValueColumn, b: PivotLayoutValueColumn): boolean =>
  a.measure.field === b.measure.field &&
  a.measure.aggregation === b.measure.aggregation &&
  a.columnPath.length === b.columnPath.length &&
  a.columnPath.every((member, level) => {
    const other = b.columnPath[level]
    return (
      member.field === other.field && member.dateGroup === other.dateGroup && toLower(member.key) === toLower(other.key)
    )
  })

/** Resolves a value column to its live column key and measure index. */
export const resolvePivotValueColumn = (
  result: PivotResult,
  valueColumn: PivotLayoutValueColumn
): { columnKey: string; measureIndex: number } | undefined => {
  const measureIndex = result.measures.findIndex(
    (measure) =>
      measure.field === valueColumn.measure.field &&
      (isPivotCalculatedMeasure(measure) ? undefined : measure.aggregation) === valueColumn.measure.aggregation
  )
  if (measureIndex === -1) return undefined
  if (valueColumn.columnPath.length > result.columnFields.length) return undefined
  let node = result.columnRoot
  for (let level = 0; level < valueColumn.columnPath.length; level++) {
    const member = valueColumn.columnPath[level]
    const field = result.columnFields[level]
    if (!field || field.field !== member.field || field.dateGroup !== member.dateGroup) return undefined
    const child = node.children.find((item) => toLower(item.member.key) === toLower(member.key))
    if (!child) return undefined
    node = child
  }
  return { columnKey: node.key, measureIndex }
}
